import React, { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    IconButton,
    Alert,
    CircularProgress,
    Avatar
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import { useAdminCreateBanner, useAdminUpdateBanner, useAdminGetBanner } from 'hooks/useBanners';
import fileService from 'api/fileService';

export default function BannerForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = !!id;

    const [formData, setFormData] = useState({
        name: '',
        link: '',
        status: 'active',
        type: 'home',
        image: '' // This will correspond to the image URL
    });
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');

    const { data: bannerData, isLoading: isLoadingBanner } = useAdminGetBanner(id);
    const createMutation = useAdminCreateBanner();
    const updateMutation = useAdminUpdateBanner();

    useEffect(() => {
        if (bannerData) {
            // Handle potential nested data structure or direct object
            const banner = bannerData.data || bannerData;

            setFormData({
                name: banner.name || banner.title || '',
                link: banner.link || '',
                // Normalize status: backend might send boolean or string, we use 'active'/'inactive'
                status: banner.status === 'active' || banner.isActive === true ? 'active' : 'inactive',
                type: banner.type || 'home_top',
                image: banner.image || banner.imageUrl || ''
            });
        }
    }, [bannerData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleImageChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            setUploading(true);
            try {
                const image = await fileService.uploadFile(file);
                setFormData((prev) => ({ ...prev, image: image.url }));
            } catch (err) {
                console.error('File upload failed', err);
                setError('Failed to upload image');
            } finally {
                setUploading(false);
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!formData.image) {
            setError('Please upload an image for the banner.');
            return;
        }

        try {
            if (isEditMode) {
                await updateMutation.mutateAsync({ id, data: formData });
            } else {
                await createMutation.mutateAsync(formData);
            }
            navigate('/admin/banners');
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to save banner.');
        }
    };

    if (isEditMode && isLoadingBanner) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box p={3} sx={{ background: '#f5f6fa', minHeight: '100vh' }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={3}>
                <IconButton onClick={() => navigate('/admin/banners')}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4" fontWeight={700}>
                    {isEditMode ? 'Edit Banner' : 'Create Banner'}
                </Typography>
            </Stack>

            <Paper sx={{ p: 4, maxWidth: 800, mx: 'auto', borderRadius: 2 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>
                                Banner Details
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar
                                    src={typeof formData.image === 'string' ? formData.image : ''}
                                    variant="rounded"
                                    sx={{ width: 200, height: 100, bgcolor: 'grey.300' }}
                                >
                                    {!formData.image && 'Preview'}
                                </Avatar>
                                <Button variant="outlined" component="label" disabled={uploading}>
                                    {uploading ? 'Uploading...' : 'Upload Image'}
                                    <input type="file" hidden accept="image/*" onChange={handleImageChange} />
                                </Button>
                            </Stack>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField fullWidth label="Banner Name" name="name" value={formData.name} onChange={handleChange} required />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Type</InputLabel>
                                <Select value={formData.type} label="Type" name="type" onChange={handleChange}>
                                    <MenuItem value="home">Home</MenuItem>
                                    <MenuItem value="scroll">Scroll</MenuItem>
                                    <MenuItem value="popup">Popup</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select value={formData.status} label="Status" name="status" onChange={handleChange}>
                                    <MenuItem value="active">Active</MenuItem>
                                    <MenuItem value="inactive">Inactive</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Link URL (Optional)"
                                name="link"
                                value={formData.link}
                                onChange={handleChange}
                                placeholder="https://example.com/promotion"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Button
                                type="submit"
                                variant="contained"
                                size="large"
                                fullWidth
                                disabled={createMutation.isPending || updateMutation.isPending || uploading}
                            >
                                {createMutation.isPending || updateMutation.isPending ? <CircularProgress size={24} /> : 'Save Banner'}
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Paper>
        </Box>
    );
}
